import React, { Fragment } from 'react';
import classNames from 'classnames';
import { NavLink } from 'react-router-dom';
import * as analytics from 'analytics';
import { isMobile, isTouch } from 'util/device';
import CONTENT_TYPES from 'globalConst/contentTypes-const';

import { DisplayLayout } from 'components/Collection/Collection.const';
import IconChevron from 'components/svg/ChevronBasic';
import { ShowMoreButtonTypes, ButtonText } from './ShowMoreButton.const';
import styles from './ShowMoreButton.styles';
import { createUseStyles } from 'react-jss';

const chevronStyles = {
  marginLeft: '0.4em',
  height: 10,
  width: 5,
  verticalAlign: 0,
};

interface ShowMoreButtonProps {
  isCarousel?: boolean;
  enableCollectionModal?: boolean;
  showMoreButton?: Array<unknown>;
  showMoreButtonAsCTA?: boolean;
  linkedPage?: string;
  toggleCollectionModal: () => void;
  showMoreLabel?: string;
  isTilesWithBackground?: boolean;
  isExpandable?: boolean;
  isExpanded?: boolean;
  itemsNotShown?: number;
  contentTypeId?: string;
  display?: string;
  showMore: {
    variant?: string;
    label?: string;
  };
  hideShowMore?: boolean;
  children: JSX.Element;
}

const useStyles = createUseStyles(styles, { name: 'ShowMoreButton' });

const ShowMoreButton = ({
  contentTypeId,
  linkedPage,
  isCarousel,
  isExpanded,
  enableCollectionModal,
  showMore = {},
  showMoreLabel,
  display,
  itemsNotShown,
  hideShowMore,
  isTilesWithBackground,
  showMoreButton,
  showMoreButtonAsCTA,
  isExpandable,
  toggleCollectionModal,
  children,
}: ShowMoreButtonProps): JSX.Element => {
  const classes = useStyles();

  const getVariantFromShowMoreButtonTypes = () => {
    if (isExpandable) return ShowMoreButtonTypes.BUTTON;
    if (showMoreButtonAsCTA) return ShowMoreButtonTypes.CTA;
    if (showMoreButton && showMoreButton.includes(ShowMoreButtonTypes.LINK)) return ShowMoreButtonTypes.LINK;
    if (showMoreButton && showMoreButton.includes(ShowMoreButtonTypes.BUTTON)) return ShowMoreButtonTypes.BUTTON;
    return null;
  };

  const { variant, label } =
    showMore && showMore.variant
      ? showMore
      : {
          // the config below is deprecated and will be removed soon
          variant: getVariantFromShowMoreButtonTypes() || ShowMoreButtonTypes.LINK,
          label: showMoreLabel,
        };

  // TODO: create interface for `analytics` type
  const navLink = (pathname: string, eventValue: string): { pathname: string; state: { analytics: unknown } } => ({
    pathname,
    state: {
      analytics: analytics.generateShowMoreSelectData(eventValue),
    },
  });

  const getText = () => {
    if (isExpandable) {
      const itemType = contentTypeId === CONTENT_TYPES.ON_DEMAND_COLLECTION ? 'shows' : 'radiozenders';
      const singularOrPluralItems = itemsNotShown === 1 ? itemType.substring(0, itemType.length - 1) : itemType;
      return isExpanded ? 'Toon minder' : `Toon andere ${itemsNotShown} ${singularOrPluralItems}`;
    }

    if (label) {
      return label;
    }

    if (linkedPage && isCarousel) {
      return isTouch ? ButtonText.ALLES : ButtonText.TOONALLES;
    }

    return isTouch ? ButtonText.MEER : ButtonText.TOONMEER;
  };

  const renderText = () => {
    const isLink = variant === ShowMoreButtonTypes.LINK || variant === ShowMoreButtonTypes.TEXT_LINK;

    const textClass = classNames({
      [classes.linkText]: isLink,
      [classes.buttonText]: variant === ShowMoreButtonTypes.BUTTON,
      [classes.ctaText]: variant === ShowMoreButtonTypes.CTA,
    });

    return (
      <Fragment>
        <span className={textClass}>{getText()}</span>
        {isLink && <IconChevron style={chevronStyles} />}
      </Fragment>
    );
  };

  const renderButton = () => {
    if ((isExpandable && itemsNotShown < 1) || hideShowMore) {
      return null;
    }

    const itemClass = classNames({
      [classes.link]: variant === ShowMoreButtonTypes.LINK || variant === ShowMoreButtonTypes.TEXT_LINK,
      [classes.button]: variant === ShowMoreButtonTypes.BUTTON,
      [classes.greenButton]: variant === ShowMoreButtonTypes.BUTTON && isMobile,
      [classes.cta]: variant === ShowMoreButtonTypes.CTA,
      [classes.buttonTilesWitGrayBackground]: isTilesWithBackground,
      [classes.buttonTilesWithWhiteBackground]:
        display === DisplayLayout.LIST && contentTypeId === 'podcastCollection' && isMobile,
      [classes.ctaTilesWithBackground]: isTilesWithBackground,
    });

    const toggleModal = () => toggleCollectionModal();
    return (
      <Fragment>
        {linkedPage && (
          <NavLink className={itemClass} to={navLink(`/${linkedPage}`, linkedPage)} exact data-testid="show_more_page">
            {renderText()}
          </NavLink>
        )}

        {!linkedPage && enableCollectionModal && (
          <button
            type="button"
            className={itemClass}
            onClick={toggleModal}
            data-testid={`${isExpanded ? 'hide' : 'show'}_more_modal`}
          >
            {renderText()}
          </button>
        )}
      </Fragment>
    );
  };

  return (
    <div className={classes.relative}>
      {(variant === ShowMoreButtonTypes.LINK || variant === ShowMoreButtonTypes.TEXT_LINK) && renderButton()}
      {children}
      {(variant === ShowMoreButtonTypes.BUTTON || variant === ShowMoreButtonTypes.CTA) && renderButton()}
    </div>
  );
};

export default ShowMoreButton;
